/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { service } from '@ember/service';
import { action } from '@ember/object';
import { tracked } from '@glimmer/tracking';
import { format as formatDate } from 'my-phorest/utils/local-date-helpers';
import { format as formatTime } from 'my-phorest/utils/local-time-helpers';
import { BREAKPOINT_MD, isAboveBreakpoint } from 'my-phorest/utils/breakpoints';
import { dropTask } from 'ember-concurrency';
import { isBlank } from '@ember/utils';
import userByPinQuery from 'my-phorest/gql/queries/user-by-pin.graphql';
import { queryManager } from 'ember-apollo-client';
import { variation } from 'ember-launch-darkly';

const SNOWFALL_STORAGE_KEY = 'sidebar-snowfall-enabled';

export default class SidebarController extends Component {
  @service branchTime;
  @service clockInOut;
  @service electronApp;
  @service eventPropagator;
  @service intl;
  @service keypad;
  @service notifications;
  @service revisionKey;
  @service router;
  @service session;
  @service('browser/window') window;
  @service('browser/local-storage') localStorage;

  @queryManager apollo;

  @tracked isSnowfallEnabled;

  constructor() {
    super(...arguments);
    // Load snowfall enabled state from localStorage, default to true if not set
    try {
      const stored = this.localStorage.getItem(SNOWFALL_STORAGE_KEY);
      this.isSnowfallEnabled = stored !== null ? stored === 'true' : true;
    } catch (e) {
      // Fall back to default if localStorage is unavailable (SSR, private browsing, etc.)
      this.isSnowfallEnabled = true;
    }
  }

  get branchFormattedTime() {
    if (!variation('release-p10-new-design'))
      return formatTime(this.branchTime.time, this.intl, 'long');

    if (this.args.isOpen) {
      return formatTime(this.branchTime.time, this.intl, 'long');
    } else {
      return formatTime(this.branchTime.time, this.intl, 'long')
        .replace(' am', '')
        .replace(' pm', '');
    }
  }

  get branchFormattedDate() {
    if (!variation('release-p10-new-design'))
      return formatDate(this.branchTime.today, this.intl, 'long');

    if (this.args.isOpen) {
      return formatDate(this.branchTime.today, this.intl, 'long');
    } else {
      return formatDate(this.branchTime.today, this.intl, 'tiny-without-year');
    }
  }

  get businessId() {
    return this.session.business.id;
  }

  get branchId() {
    return this.session.branch.id;
  }

  get isRunningInElectron() {
    return this.electronApp.isRunningInElectron;
  }

  get currentUserEmail() {
    return this.session.user?.email || this.session.branch?.email;
  }

  @action
  onAfterClickMenuItem() {
    if (isAboveBreakpoint(BREAKPOINT_MD)) return;

    this.args.toggle();
  }

  @dropTask
  *getUserByPin(pin) {
    if (isBlank(pin)) return false;

    let newUser = yield this.apollo.query(
      {
        query: userByPinQuery,
        variables: {
          pin,
        },
        fetchPolicy: 'network-only',
      },
      'user'
    );

    newUser = this.#findStaffForCurrentBranch(newUser, this.branchId);
    if (!newUser) {
      this.notifications.failure(
        this.intl.t('access.error-title'),
        this.intl.t('access.not-found-error')
      );
      return false;
    }
    this.clockInOut.checkClockInOut(newUser);
  }

  @action
  openPinPadAndGetUser() {
    this.keypad.openKeypad({
      headerText: this.intl.t('app-main-nav.clock-in-out'),
      value: '',
      variant: 'pin',
      onChange: this.getUserByPin.perform,
    });
  }

  #findStaffForCurrentBranch(user, branchId) {
    return user?.staff.find((staff) => staff.branch.id === branchId);
  }

  // this will be removed when removing FF release-let-it-snow
  @action
  toggleSnowfall() {
    this.isSnowfallEnabled = !this.isSnowfallEnabled;
    try {
      this.localStorage.setItem(
        SNOWFALL_STORAGE_KEY,
        String(this.isSnowfallEnabled)
      );
    } catch (e) {
      // Silently fail if localStorage is unavailable
    }
  }
}
